import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";
import ReportFull from "@/components/report/ReportFull";
import { Lock, Clock, ArrowUpRight, Sparkles } from "lucide-react";

const REFERRAL_KEY = "bl_referral_share_token";

export default function SharedReport() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Capture referral attribution
        try {
            localStorage.setItem(REFERRAL_KEY, token);
        } catch { /* non-critical */ }

        api.get(`/shared/${token}`)
            .then((r) => setData(r.data))
            .catch((e) => {
                if (e?.response?.status === 410) {
                    setError("This shared link has expired.");
                } else if (e?.response?.status === 404) {
                    setError("This shared link is no longer valid.");
                } else {
                    setError("Could not load this shared report.");
                }
            })
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) {
        return (
            <PublicLayout>
                <div className="bl-container py-24 text-center text-bl-muted">
                    <div className="bl-spinner mx-auto mb-4" /> Loading shared report…
                </div>
            </PublicLayout>
        );
    }

    if (error) {
        return (
            <PublicLayout>
                <div className="bl-container py-24 max-w-xl" data-testid="shared-error">
                    <div className="bl-eyebrow">
                        <Lock className="w-3.5 h-3.5" /> Shared link
                    </div>
                    <h1 className="mt-4 font-display font-semibold text-3xl text-bl">
                        {error}
                    </h1>
                    <p className="mt-3 text-bl-muted">
                        Ask the report owner to send a new link, or learn more about
                        Buyer's Leverage.
                    </p>
                    <Link to="/" className="bl-btn-secondary mt-6">
                        Visit Buyer's Leverage
                    </Link>
                </div>
            </PublicLayout>
        );
    }

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container pt-10 pb-4" data-testid="shared-report-page">
                    <div className="flex flex-wrap items-center gap-3 justify-between">
                        <div className="bl-eyebrow">
                            <Lock className="w-3.5 h-3.5" /> Read-only shared report
                        </div>
                        <div className="text-xs text-bl-muted flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5" />
                            Expires {new Date(data.share.expires_at).toLocaleDateString()}
                        </div>
                    </div>
                    {data.share.note && (
                        <div className="mt-4 bl-card p-4 bg-bl-teal-tint border-bl">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-primary mb-1">
                                Note from the buyer
                            </div>
                            <p className="text-bl">{data.share.note}</p>
                        </div>
                    )}
                </div>
            </section>
            <section className="bl-container pb-20">
                <ReportFull
                    report={data.report}
                    ownedTier={data.report.tier || "closing_strategy"}
                />

                {/* Lead-gen CTA */}
                <div
                    className="mt-12 bl-card p-6 sm:p-8 md:p-12 relative overflow-hidden"
                    data-testid="shared-leadgen"
                >
                    <div className="bl-eyebrow w-fit">
                        <Sparkles className="w-3.5 h-3.5" /> Get a report like this
                    </div>
                    <h3 className="mt-4 font-display font-semibold text-[1.5rem] sm:text-3xl md:text-4xl tracking-tight text-bl max-w-xl leading-tight">
                        Want a Buyer's Leverage report for{" "}
                        <span className="font-italic-accent text-bl-primary">
                            your own
                        </span>{" "}
                        inspection?
                    </h3>
                    <p className="mt-4 text-bl-muted max-w-xl leading-relaxed">
                        Upload your own inspection PDF and get the same calm, organized,
                        negotiation-ready analysis. Reports save to your dashboard and
                        export to PDF.
                    </p>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link
                            to={`/auth/register?ref=${encodeURIComponent(token)}&next=${encodeURIComponent("/upload")}`}
                            className="bl-btn-primary"
                            data-testid="shared-leadgen-cta"
                        >
                            Create my free account{" "}
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/sample-analysis"
                            className="bl-btn-secondary"
                            data-testid="shared-leadgen-sample"
                        >
                            Browse a sample report
                        </Link>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}
